import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle, AlertCircle, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PublicFormRenderer = ({ formId }) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const loadForm = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/public/forms/${formId}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError('Formulario no encontrado o inactivo');
          } else {
            setError('Error al cargar el formulario');
          }
          return;
        }
        const data = await response.json();
        setForm(data);
        
        // Initialize form values with defaults
        const initialValues = {};
        data.fields?.forEach(field => {
          initialValues[field.id] = field.default_value || '';
        });
        setFormValues(initialValues);
      } catch (err) {
        console.error('Error loading form:', err);
        setError('Error de conexión');
      } finally {
        setLoading(false);
      }
    };

    if (formId) {
      loadForm();
    }
  }, [formId]);

  const validateForm = () => {
    const errors = {};
    form.fields?.forEach(field => {
      if (field.required && !formValues[field.id]?.trim()) {
        errors[field.id] = 'Este campo es requerido';
      }
      if (field.field_type === 'email' && formValues[field.id]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formValues[field.id])) {
          errors[field.id] = 'Email inválido';
        }
      }
      if (field.validation_regex && formValues[field.id]) {
        try {
          const regex = new RegExp(field.validation_regex);
          if (!regex.test(formValues[field.id])) {
            errors[field.id] = 'Formato inválido';
          }
        } catch (e) {
          // Invalid regex, skip validation
        }
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/public/forms/${formId}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: formValues,
          source: window.location.href
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Error al enviar el formulario');
      }

      setSubmitted(true);

      // Redirect if configured
      if (result.redirect_url) {
        setTimeout(() => {
          window.location.href = result.redirect_url;
        }, 2000);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.message || 'Error al enviar el formulario');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (fieldId, value) => {
    setFormValues(prev => ({ ...prev, [fieldId]: value }));
    if (validationErrors[fieldId]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#f5f5f5' }}
      >
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto text-gray-400 mb-4" size={40} />
          <p className="text-gray-500">Cargando formulario...</p>
        </div>
      </div>
    );
  }

  if (error && !form) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#f5f5f5' }}
      >
        <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
          <AlertCircle className="mx-auto text-red-400 mb-4" size={48} />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: form?.background_color || '#f5f5f5' }}
      >
        <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: form?.primary_color || '#D4A843' }}
          >
            <CheckCircle className="text-white" size={32} />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">¡Enviado!</h2>
          <p className="text-gray-600">{form?.success_message || '¡Gracias! Tu información ha sido recibida.'}</p>
          {form?.redirect_url && (
            <p className="text-sm text-gray-400 mt-4">Redirigiendo...</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen py-8 px-4"
      style={{ backgroundColor: form?.background_color || '#f5f5f5' }}
      data-testid="public-form-container"
    >
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div 
            className="p-6 text-white"
            style={{ backgroundColor: form?.primary_color || '#D4A843' }}
          >
            {form?.logo_url && (
              <div className="mb-4">
                <img 
                  src={form.logo_url} 
                  alt="Logo" 
                  className="h-10 object-contain"
                />
              </div>
            )}
            <h1 className="text-2xl font-bold">{form?.name}</h1>
            {form?.description && (
              <p className="text-white/80 mt-2">{form.description}</p>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {form?.fields?.map(field => {
              if (field.field_type === 'hidden') {
                return (
                  <input 
                    key={field.id}
                    type="hidden" 
                    name={field.id}
                    value={formValues[field.id] || field.default_value || ''}
                  />
                );
              }

              return (
                <div key={field.id} className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  
                  {field.field_type === 'textarea' ? (
                    <textarea
                      value={formValues[field.id] || ''}
                      onChange={e => handleInputChange(field.id, e.target.value)}
                      placeholder={field.placeholder}
                      rows={4}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-opacity-50 transition-colors ${
                        validationErrors[field.id] 
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                          : 'border-gray-300 focus:border-gray-400 focus:ring-gray-200'
                      }`}
                      style={validationErrors[field.id] ? {} : { '--tw-ring-color': form?.primary_color }}
                      data-testid={`field-${field.id}`}
                    />
                  ) : field.field_type === 'select' ? (
                    <select
                      value={formValues[field.id] || ''}
                      onChange={e => handleInputChange(field.id, e.target.value)}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-opacity-50 transition-colors ${
                        validationErrors[field.id] 
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                          : 'border-gray-300 focus:border-gray-400 focus:ring-gray-200'
                      }`}
                      data-testid={`field-${field.id}`}
                    >
                      <option value="">{field.placeholder || 'Seleccionar...'}</option>
                      {field.options?.map((option, idx) => (
                        <option key={idx} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : field.field_type === 'checkbox' ? (
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formValues[field.id] === 'true' || formValues[field.id] === true}
                        onChange={e => handleInputChange(field.id, e.target.checked ? 'true' : '')}
                        className="w-5 h-5 rounded border-gray-300 text-gray-600 focus:ring-2"
                        style={{ accentColor: form?.primary_color }}
                        data-testid={`field-${field.id}`}
                      />
                      <span className="text-sm text-gray-600">{field.placeholder || field.label}</span>
                    </label>
                  ) : (
                    <input
                      type={field.field_type}
                      value={formValues[field.id] || ''}
                      onChange={e => handleInputChange(field.id, e.target.value)}
                      placeholder={field.placeholder}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-opacity-50 transition-colors ${
                        validationErrors[field.id] 
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                          : 'border-gray-300 focus:border-gray-400 focus:ring-gray-200'
                      }`}
                      data-testid={`field-${field.id}`}
                    />
                  )}

                  {field.help_text && (
                    <p className="text-xs text-gray-500">{field.help_text}</p>
                  )}
                  
                  {validationErrors[field.id] && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {validationErrors[field.id]}
                    </p>
                  )}
                </div>
              );
            })}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-lg text-white font-medium transition-all hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ backgroundColor: form?.primary_color || '#D4A843' }}
              data-testid="submit-form-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Enviando...
                </>
              ) : (
                <>
                  <Send size={18} />
                  {form?.submit_button_text || 'Enviar'}
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="px-6 pb-4 text-center">
            <p className="text-xs text-gray-400">
              Powered by Infinito CRM
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicFormRenderer;
